import { useEffect, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PestReport } from "@/components/PestReport";
import { PlantingCalendar } from "@/components/PlantingCalendar";
import { Replies } from "@/components/Replies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Copy, Bug, CalendarDays, ShieldAlert, MessageSquare, Trash2, Loader2, LogOut, Send, Eye } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { pest_report: Bug, field_calendar: CalendarDays, pest_risk: ShieldAlert, note: MessageSquare };
const ago = (iso) => { const d = Math.round((Date.now() - new Date(iso).getTime()) / 86400000); return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`; };

function SharePicker({ groupId, onPosted }) {
  const [kind, setKind] = useState("note");
  const [refId, setRefId] = useState("");
  const [note, setNote] = useState("");
  const [reports, setReports] = useState([]);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiClient.get("/pest/reports").then((r) => setReports(r.data)).catch(() => {});
    apiClient.get("/fields").then((r) => setFields(r.data)).catch(() => {});
  }, []);

  const options = kind === "pest_report" ? reports.map((r) => ({ id: r.id, label: `${r.result?.problem}${r.crop ? ` · ${r.crop}` : ""}` }))
    : kind === "field_calendar" ? fields.map((f) => ({ id: f.id, label: f.name }))
    : kind === "pest_risk" ? fields.filter((f) => f.pest_risk).map((f) => ({ id: f.id, label: `${f.name} · ${f.pest_risk.overall_risk}` })) : [];

  const post = async () => {
    if (kind !== "note" && !refId) return toast.error("Pick what to share");
    setBusy(true);
    try {
      const r = await apiClient.post(`/groups/${groupId}/posts`, { kind, ref_id: refId || null, note });
      onPosted(r.data); setNote(""); setRefId(""); toast.success("Shared with the group");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4" data-testid="group-share">
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Share with the group</div>
      <div className="grid sm:grid-cols-[170px_1fr] gap-2">
        <Select value={kind} onValueChange={(k) => { setKind(k); setRefId(""); }}>
          <SelectTrigger data-testid="share-kind-select" className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="note">A note</SelectItem>
            <SelectItem value="pest_report">Pest diagnosis</SelectItem>
            <SelectItem value="field_calendar">Planting calendar</SelectItem>
            <SelectItem value="pest_risk">Weekly pest risk</SelectItem>
          </SelectContent>
        </Select>
        {kind !== "note" && (
          <Select value={refId} onValueChange={setRefId}>
            <SelectTrigger data-testid="share-ref-select" className="bg-white"><SelectValue placeholder={options.length ? "Choose…" : "Nothing to share yet"} /></SelectTrigger>
            <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <div className="flex gap-2 mt-2">
        <Input data-testid="share-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === "note" ? "What's happening on your farm?" : "Add a note (optional)"} className="bg-white" />
        <Button data-testid="share-post-btn" onClick={post} disabled={busy} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
      </div>
    </div>
  );
}

function Post({ p, canDelete, onDelete, groupId, replies, onReply }) {
  const [open, setOpen] = useState(false);
  const Icon = KIND_ICON[p.kind] || MessageSquare;
  return (
    <li data-testid={`group-post-${p.id}`} className="bg-white rounded-2xl border border-stone-200 p-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[#EAF6EF] text-[#2D6A4F] flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-stone-500"><b className="text-emerald-950">{p.author}</b> · {ago(p.created_at)}</div>
          <div className="font-semibold text-emerald-950">{p.title}</div>
          {p.note && <p className="text-sm text-stone-700 mt-1">{p.note}</p>}
          {p.kind !== "note" && <button data-testid={`post-open-${p.id}`} onClick={() => setOpen((o) => !o)} className="text-sm font-semibold text-[#2D6A4F] mt-2 inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {open ? "Hide" : "Open"}</button>}
          <Replies groupId={groupId} target={`post:${p.id}`} replies={replies} onReply={onReply} />
        </div>
        {canDelete && <button data-testid={`post-delete-${p.id}`} onClick={() => onDelete(p)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>}
      </div>
      {open && (
        <div className="mt-4">
          {p.kind === "pest_report" && <PestReport report={{ ...p.data, id: null }} readOnly />}
          {p.kind === "field_calendar" && <PlantingCalendar crops={p.data?.crops} />}
          {p.kind === "pest_risk" && (
            <div className="rounded-xl border border-stone-200 p-4 text-sm space-y-2">
              <div><b>{p.data?.risk?.overall_risk} risk</b> — {p.data?.risk?.summary}</div>
              <ul className="list-disc pl-5 text-stone-700">{(p.data?.risk?.warnings || []).map((w, i) => <li key={i}><b>{w.problem}</b> ({w.risk}): {w.action}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function GroupFeed({ group, onBack, onLeft }) {
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [replies, setReplies] = useState({});

  useEffect(() => {
    apiClient.get(`/groups/${group.id}`).then((r) => setG(r.data)).catch(() => toast.error("Could not load group"));
    apiClient.get(`/groups/${group.id}/replies`).then((r) => setReplies(r.data)).catch(() => {});
  }, [group.id]);
  const addReply = (target, r) => setReplies((m) => ({ ...m, [target]: [...(m[target] || []), r] }));

  const copy = async () => { try { await navigator.clipboard.writeText(g.code); toast.success("Code copied"); } catch { toast.error(`Code: ${g.code}`); } };
  const leave = async () => { try { await apiClient.post(`/groups/${group.id}/leave`); toast.success("You left the group"); onLeft(); } catch (e) { toast.error(e.message); } };
  const del = async (p) => { try { await apiClient.delete(`/groups/${group.id}/posts/${p.id}`); setG((x) => ({ ...x, posts: x.posts.filter((y) => y.id !== p.id) })); } catch { toast.error("Could not delete"); } };

  if (!g) return <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading group…</div>;
  return (
    <div data-testid="group-feed">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <Button data-testid="group-back-btn" variant="ghost" onClick={onBack} className="text-[#2D6A4F]"><ChevronLeft className="h-4 w-4 mr-1" /> All groups</Button>
        <div className="flex items-center gap-2">
          <button data-testid="group-code-btn" onClick={copy} className="inline-flex items-center gap-1.5 text-sm bg-white border border-stone-200 rounded-full px-3 py-1.5 hover:border-[#2D6A4F]">Invite code <span className="font-mono-tech font-bold text-[#78350F]">{g.code}</span> <Copy className="h-3.5 w-3.5" /></button>
          <Button data-testid="group-leave-btn" variant="outline" size="sm" onClick={leave} className="border-stone-300 text-red-600"><LogOut className="h-3.5 w-3.5 mr-1" /> Leave</Button>
        </div>
      </div>
      <div className="bg-[#143D29] text-emerald-50 rounded-2xl p-6 mb-6">
        <h2 className="font-display text-2xl font-bold" data-testid="group-title">{g.name}</h2>
        <div className="text-sm text-emerald-100/80">{g.village ? `${g.village} · ` : ""}{g.members} member{g.members !== 1 ? "s" : ""}: {g.member_list.map((m) => m.name).join(", ")}</div>
      </div>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-4">
          <SharePicker groupId={g.id} onPosted={(p) => setG((x) => ({ ...x, posts: [p, ...x.posts] }))} />
          {g.posts.length === 0 ? <p data-testid="posts-empty" className="text-sm text-stone-500 text-center py-6">Nothing shared yet — be the first.</p>
            : <ul className="space-y-3" data-testid="group-posts">{g.posts.map((p) => <Post key={p.id} p={p} groupId={g.id} replies={replies[`post:${p.id}`] || []} onReply={addReply} canDelete={g.is_owner || p.user_id === user?.id} onDelete={del} />)}</ul>}
        </div>
        <aside className="bg-white rounded-2xl border border-stone-200 p-4 h-fit" data-testid="group-sightings">
          <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2 flex items-center gap-1"><Bug className="h-3.5 w-3.5 text-[#78350F]" /> Village sightings · 30 days</div>
          {g.sightings.length === 0 ? <p className="text-sm text-stone-500">No pest or disease sightings from members yet.</p> : (
            <ul className="space-y-2">
              {g.sightings.map((s) => (
                <li key={s.id} className="text-sm border-l-2 pl-3" style={{ borderColor: (s.severity || "").toLowerCase() === "high" ? "#B91C1C" : "#D97706" }}>
                  <div className="font-semibold text-emerald-950">{s.problem}{s.crop ? <span className="text-stone-500 font-normal"> · {s.crop}</span> : null}</div>
                  <div className="text-xs text-stone-500">{s.mine ? "You" : s.by} · {s.severity} · {ago(s.created_at)}</div>
                  <Replies groupId={g.id} target={`sighting:${s.id}`} replies={replies[`sighting:${s.id}`] || []} onReply={addReply} compact />
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
